import React, { useState, useEffect } from 'react';
import { useMediaQuery } from '@mui/material';
import { 
  Box, 
  Breadcrumbs,
  Typography, 
  List, 
  ListItem, 
  Divider,
  Button,
  IconButton
} from '@mui/material';
import { 
  KeyboardArrowDown, 
  KeyboardArrowRight,
  KeyboardArrowUp
} from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import { axiosRequest } from '../../helpers/axios';
import styles from './OrderStatus.module.css';
import { pgOrderHistory } from '../mixPanelEvents/mixPanelEvents';

interface PurchaseItem {
  order_id: string;
  purchase_id: string;
  product_id: string;
  product_name: string;
  purchase_status: string;
  purchase_date: string;
  variant_name?: string;
  shipFirstName?: string;
  shipLastName?: string;
  shipAddress1?: string;
  shipAddress2?: string;
  shipCity?: string;
  shipState?: string;
  shipCountry?: string;
  shipPostalCode?: string;
  emailAddress?: string;
  phoneNumber?: string;
  image_path?: string;
  tracking_number?: string;
  shipment_status?: string;
  isDigital?: boolean;
  coc_product_id?: string;
  course_url?: string;
  carrier_url?: string;
}

// interface OrderItem {
//   id: string;
//   name: string;
//   status: 'in_progress' | 'shipped' | 'delivered' | 'digital';
//   statusText: string;
//   orderDate: string;
//   action: 'see_details' | 'access_now' | 'loading';
//   actionText: string;
//   isDigital: boolean;
//   purchaseData?: PurchaseItem;
// }

// for testing

interface OrderItem {
  id: string;
  name: string;
  status: 'in_progress' | 'shipped' | 'delivered' | 'digital';
  statusText: string;
  orderDate: string;
  action: 'see_details' | 'access_now' | 'loading';
  actionText: string;
  isDigital: boolean;
  purchaseData?: Partial<PurchaseItem>;
}


const OrderStatus: React.FC = () => {
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [orders, setOrders] = useState<OrderItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [popupOrder, setPopupOrder] = useState<OrderItem | null>(null);
  const isMobile = useMediaQuery('(max-width:649px)');
  // Track which orders have animated step progress
  const [expandedAnimatedIds, setExpandedAnimatedIds] = useState<Set<string>>(new Set());

  const breadcrumbs = (
    <Breadcrumbs
      aria-label="breadcrumb"
      separator="/"
      sx={{
        marginBottom: "16px",
        "& li": {
          display: "flex",
          margin: "0",
          fontWeight: 400,
          fontSize: "16px",
          lineHeight: "122%",
          letterSpacing: "-0.16px",
        },
        "& .MuiBreadcrumbs-separator": {
          margin: "0 2px",
          color: "#7B726C",
          lineHeight: "122%",
        },
      }}
    >
      <Button
        style={{ textTransform: "none", color: "#7B726C" }}
        sx={{
          fontFamily: "Repro !important",
          fontSize: "16px",
          fontStyle: "normal",
          fontWeight: 400,
          lineHeight: "122%",
          letterSpacing: "-0.16px",
          minWidth: "auto",
          padding: 0,
          "&:hover": {
            backgroundColor: "transparent",
            textDecoration: "underline",
          },
        }}
        variant="text"
        onClick={() => (window.location.href = "/courses")}
      >
        <svg width="12" height="14" viewBox="0 0 12 14" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M1.5 12.5H3.5V7.5H8.5V12.5H10.5V5.25L6 1.875L1.5 5.25V12.5ZM0 14V4.5L6 0L12 4.5V14H7V9H5V14H0Z" fill="#1D1A1A"/>
        </svg>

      </Button>
      <Button
        disableRipple
        disableFocusRipple
        style={{ textTransform: "none" }}
        sx={{
          fontFamily: "Repro !important",
          fontSize: "16px",
          fontStyle: "normal",
          fontWeight: 400,
          lineHeight: "122%",
          letterSpacing: "-0.16px",
          minWidth: "auto",
          padding: 0,
          "&:hover": {
            backgroundColor: "transparent",
            textDecoration: "none",
          },
          textDecoration: "none",
          color: "#1D1A1A",
          cursor: "default !important",
        }}
        variant="text"
      >
        Order History
      </Button>
    </Breadcrumbs>
  );

  useEffect(() => {
    fetchOrders();
  }, []);


// products that should be excluded
const EXCLUDED_PRODUCT_IDS = new Set<string>([
  "6","7", "8", "37", "38", "39", "44", "45", "46", "47", "48", "51", "54",
  "56", "60", "61", "63", "64", "65", "66", "67", "68", "69", "77", "78",
  "83", "85", "87", "92", "95", "96", "97", "98", "99", "100", "101",
  "102", "103", "104", "106", "108", "109", "114", "131", "132", "133",
  "136", "138", "139", "140", "141", "142", "143", "144", "145", "146",
  "150", "155", "156", "157", "158", "159", "160", "169", "170", "174",
  "175", "176", "177", "183", "184", "185", "186", "187", "188", "189",
  "190", "191", "192", "193", "194", "195", "196", "200", "201", "202",
  "211", "214", "215", "222", "223", "224", "239", "240", "241", "242",
  "250", "251", "255", "256", "264", "265", "271", "272", "275", "276",
  "322", "323", "332", "333", "354", "355", "356", "359", "360", "367",
  "368", "371", "379", "382", "384", "385", "386", "389", "390", "391",
  "416", "417", "421", "422", "426", "427", "428", "432", "441", "442",
  "443", "444", "445", "446", "447", "470", "472", "474", "476", "478",
  "480", "482", "484", "494", "496", "501", "503", "519", "525", "527",
  "529", "537"
]);

const hasAllowedProductId = (productId?: string): boolean => {
  return typeof productId === "string" && !EXCLUDED_PRODUCT_IDS.has(productId);
};

const DEFAULT_COURSE_URL = 'https://members.performancegolf.com/courses';


const fetchOrders = async () => {
  try {
    setLoading(true);

    const response = await axiosRequest("GET", "/wp-json/pg/v2/orders");

    if (response.data.success && response.data.data) {
      const formattedOrders = response.data.data
        .filter((purchase: PurchaseItem) =>
          hasAllowedProductId(purchase.product_id+'')
        )
        .map((purchase: PurchaseItem) => {
          const getStatusFromPurchase = (
            purchaseStatus: string
          ): OrderItem["status"] => {
            switch (purchaseStatus?.toUpperCase()) {
              case "ACTIVE":
              case "PENDING":
                return "in_progress";
              case "SHIPPED":
              case "FULFILLED":
                return "shipped";
              case "DELIVERED":
                return "delivered";
              case "DIGITAL":
              case "COMPLETED":
                return "digital";
              default:
                return "in_progress";
            }
          };

          const formatDate = (dateString: string) => {
            if (!dateString) return "N/A";
            try {
              const date = new Date(dateString);
              return date.toLocaleDateString("en-US", {
                month: "2-digit",
                day: "2-digit",
                year: "numeric",
              });
            } catch {
              return dateString;
            }
          };

          return {
            id: purchase.purchase_id || purchase.order_id,
            name: purchase.product_name,
            status: getStatusFromPurchase(purchase.purchase_status),
            statusText: purchase.purchase_status || "Order in Progress",
            orderDate: formatDate(purchase.purchase_date),
            action: "loading",
            actionText: "",
            isDigital: false,
            purchaseData: purchase,
          };
        });

      setOrders(formattedOrders);



      // Fetch order details in background, throttled to avoid overwhelming the DB.
      const ORDER_DETAIL_BATCH_SIZE = 10;

      void (async () => {
        for (
          let start = 0;
          start < formattedOrders.length;
          start += ORDER_DETAIL_BATCH_SIZE
        ) {
          const batch = formattedOrders.slice(
            start,
            start + ORDER_DETAIL_BATCH_SIZE
          );

          await Promise.allSettled(
            batch.map(async (order: OrderItem) => {
              try {
                const orderDetailResponse = await axiosRequest(
                  "GET",
                  `/wp-json/pg/v2/orderdetail?orderId=${encodeURIComponent(
                    order.purchaseData?.order_id || ""
                  )}&productId=${encodeURIComponent(
                    order.purchaseData?.coc_product_id || ""
                  )}&actualProductId=${encodeURIComponent(
                    order.purchaseData?.product_id || ""
                  )}`
                );

                if (
                  orderDetailResponse.data.success &&
                  orderDetailResponse.data.data
                ) {
                  const orderDetail = orderDetailResponse.data.data;

                  setOrders((prevOrders) =>
                    prevOrders.map((prevOrder) =>
                      prevOrder.id === order.id
                        ? {
                            ...prevOrder,
                            action: orderDetail.isDigital
                              ? "access_now"
                              : "see_details",
                            actionText: orderDetail.isDigital
                              ? "Access Now"
                              : "See Details",
                            isDigital: orderDetail.isDigital,
                            purchaseData: {
                              ...prevOrder.purchaseData!,
                              ...(orderDetail.image_path != null && {
                                image_path: orderDetail.image_path,
                              }),
                              ...(orderDetail.category_name != null && {
                                category_name: orderDetail.category_name,
                              }),
                              ...(orderDetail.tracking_number != null && {
                                tracking_number: orderDetail.tracking_number,
                              }),
                              ...(orderDetail.shipment_status != null && {
                                shipment_status:
                                  orderDetail.shipment_status,
                              }),
                              ...(orderDetail.isDigital !== undefined && {
                                isDigital: orderDetail.isDigital,
                              }),
                              ...(orderDetail.course_url
                                ? { course_url: orderDetail.course_url }
                                : { course_url: DEFAULT_COURSE_URL }),
                              ...(orderDetail.carrier_url != null && {
                                carrier_url: orderDetail.carrier_url,
                              }),
                            },
                          }
                        : prevOrder
                    )
                  );
                }
              } catch (error) {
                console.error(
                  `Error fetching order detail for order ${order.id}:`,
                  error
                );
              }
            })
          );
        }
      })();

      setOrders((prevOrders) =>
        prevOrders.map((order) => ({
          ...order,
          purchaseData: {
            ...order.purchaseData!,
            course_url:
              typeof order.purchaseData?.course_url === "string" &&
              order.purchaseData.course_url.trim() !== ""
                ? order.purchaseData.course_url
                : DEFAULT_COURSE_URL,
          },
        }))
      );
    } else {
      setOrders([]);
    }
  } catch (err) {
    console.error("Error fetching orders:", err);
    setError(err instanceof Error ? err.message : "Failed to fetch orders");
    setOrders(getMockOrders());
  } finally {
    setLoading(false);
  }
};



// console.log("orders after transformation ===> ", orders)

  // Fallback mock data
  const getMockOrders = (): OrderItem[] => [
    {
      id: '1',
      name: '357 Fairway Hybrid',
      status: 'in_progress',
      statusText: 'Order in Progress',
      orderDate: '08/11/2025',
      action: 'see_details',
      actionText: 'See Details',
      isDigital: false
    },
    {
      id: '2',
      name: 'VIP Coaching',
      status: 'digital',
      statusText: '',
      orderDate: '08/11/2025',
      action: 'access_now',
      actionText: 'Access Now',
      isDigital: true
    }
  ];

  const getActionIcon = (action: OrderItem['action'], isExpanded: boolean) => {
    if (action === 'loading') {
      return <div className={styles.loadingSpinner}></div>;
    }
    if (action === 'see_details') {
      return isExpanded ? 
        <KeyboardArrowUp className={styles.actionIcon} /> : 
        <KeyboardArrowDown className={styles.actionIcon} />;
    }
    return <KeyboardArrowRight className={styles.actionIcon} />;
  };

  const handleActionClick = (order: OrderItem) => {
    if(order.action === 'access_now'){
      pgOrderHistory({
        "Product Type": "Digital",
        "Product Name": order.name,
        "Product CTA": "Access Now"
      });
    }
    if (order.purchaseData?.product_id == '1') {
      // Redirect Product #1 to scratch club homepage
      window.location.href = `${window.location.origin}/scratch-club`;
      return;
    }
    else if (order.purchaseData?.product_id == '124' || order.purchaseData?.product_id == '125') {
      // Redirect Product #1 to scratch club homepage
      window.location.href = `${window.location.origin}/courses`;
      return;
    }
    else if (order.purchaseData?.product_id == '2') {
      // Redirect Product #1 to scratch club homepage
      window.location.href = `${window.location.origin}/vip-coaching`;
      return;
    }
    else if (order.action === 'access_now' && order.purchaseData?.course_url) {
      window.location.href = order.purchaseData.course_url;
      return;
    } else if (order.action === 'access_now') {
      // No course URL, fallback to details expansion
    } else {
      setExpandedItems(prev => {
        const newSet = new Set(prev);
        if (newSet.has(order.id)) {
          newSet.delete(order.id);
          // Collapsing: remove animation state
          setExpandedAnimatedIds(prevIds => {
            const updated = new Set(prevIds);
            updated.delete(order.id);
            return updated;
          });
        } else {
          pgOrderHistory({
            "Product Type": "Physical",
            "Product Name": order.name,
            "Product CTA": "See Details"
          });
          newSet.add(order.id);
          // Expanding: trigger animation after mount
          setTimeout(() => {
            setExpandedAnimatedIds(prevIds => new Set(prevIds).add(order.id));
          }, 50);
        }
        return newSet;
      });
    }
  };

  const isExpanded = (orderId: string) => expandedItems.has(orderId);

  const handleOpenPopup = (order: OrderItem) => {
    setPopupOrder(order);
  };
  const handleClosePopup = () => {
    setPopupOrder(null);
  };

  // Format tracking number: show last 9 digits if longer than 9 chars
  const formatTrackingNumber = (trackingNumber: string) => {
    if (!trackingNumber || trackingNumber.length <= 9) {
      return trackingNumber;
    }
    
    const lastPart = trackingNumber.substring(trackingNumber.length - 9);
    return `...${lastPart}`;
  };

  if (loading) {
    return (
      <Box className={styles.container}>
        <Box className={styles.mainContent}>
          {breadcrumbs}
          <Typography variant="h2" className={styles.pageTitle}>
            Order History
          </Typography>
          <Typography>Loading orders...</Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className={styles.container}>
        <Box className={styles.mainContent}>
          {breadcrumbs}
          <Typography variant="h2" className={styles.pageTitle}>
            Order History
          </Typography>
          <Typography color="error">Error: {error}</Typography>
          <Button onClick={fetchOrders} variant="contained" style={{marginTop: '16px'}}>
            Retry
          </Button>
        </Box>
      </Box>
    );
  }

  // Specific filter: for Scratch Club (product_id '1'), if any non-cancelled exists, drop cancelled; else show only first cancelled
  const scratchClubOrders = orders.filter(o => o.purchaseData?.product_id == '1');
  const hasValidScratch = scratchClubOrders.some(o => o.purchaseData?.purchase_status?.toUpperCase() !== 'CANCELLED');
  let displayedOrders = orders;
  if (hasValidScratch) {
    displayedOrders = orders.filter(o => !(o.purchaseData?.product_id == '1' && o.purchaseData?.purchase_status?.toUpperCase() === 'CANCELLED'));
  } else {
    let seen = false;
    displayedOrders = orders.filter(o => {
      if (o.purchaseData?.product_id == '1' && o.purchaseData?.purchase_status?.toUpperCase() === 'CANCELLED') {
        if (!seen) { seen = true; return true; }
        return false;
      }
      return true;
    });
  }
  return (
    <>
    <Box 
      className={`${styles.container} maxWidthOverflow`}
      sx={{ margin: "0 auto" }}
    >
      <Box className={styles.mainContent}>
        {breadcrumbs}
        <Typography className={styles.pageTitle} style={{ fontSize: '24px', fontWeight: '600' }}>
          Order History
        </Typography>
        {/* <hr className={styles.hr}/> */}
        
        {displayedOrders.length > 0 && (
          <Box className={styles.emailMessage}>
            <Typography className={styles.emailText}>
              We've sent detailed information about the order confirmation and delivery to <br className={styles.brMobile}/>
              <strong>{window.pgUser.user_email}</strong>.
            </Typography>
          </Box>
        )}
        
        {displayedOrders.length === 0 ? (
          <Box style={{textAlign: 'center', marginTop: isMobile ? '32px' : '41px'}}>
            <Typography 
              style={{
                color: 'var(--Text-primary, #000)',
                textAlign: 'center',
                fontFamily: '"Repro"',
                fontSize: isMobile ? '16px' : '20px',
                fontStyle: 'normal',
                fontWeight: isMobile ? 400 : 700,
                lineHeight: isMobile ? '122%' : '28px',
                letterSpacing: isMobile ? '-0.16px' : 'normal'
              }}
            >
              Nothing to track yet — your cart's calling! Explore our best-selling training, clubs, and gear.
            </Typography>
          </Box>
        ) : (
          <>
            <List className={styles.orderList}>
              {displayedOrders.map((order, index) => (
                <React.Fragment key={order.id}>
                  <ListItem className={styles.orderItem}>
                    <Box className={styles.orderContainer}>
                      <Box className={`${styles.orderRow} ${expandedItems.has(order.id) ? styles.orderRowExpanded : ''}`}>
                        <Box className={styles.orderLeft}>
                          <Box className={`${styles.orderInfo} ${expandedItems.has(order.id) ? styles.orderInfoExpanded : ''}`}>
                            <Typography className={styles.orderName}>
                              {order.name}
                            </Typography>
                            <Typography
                              className={`${styles.statusText} ${styles.fixedWidth} ${(!expandedItems.has(order.id) && (order.action !== 'loading')) ? styles.statusTextCollapsedDesktop : ''}`}
                            >
                              {order.purchaseData?.purchase_status?.toUpperCase() === 'CANCELLED'
                                ? 'Status: Cancelled'
                                : order.action === 'loading'
                                ? ''
                                : order.purchaseData?.isDigital
                                ? ''
                                : `Status: ${order.purchaseData?.shipment_status ?? 'Unknown'}`
                              }
                            </Typography>
                          </Box>
                        </Box>
                        
                        <Box 
                          className={`${styles.orderRight} ${styles.noTapHighlight} ${styles.width148px} ${order.action === 'see_details' ? styles.orderRightSeeDetails : order.action === 'access_now' ? styles.orderRightAccessNow : ''}`}
                          style={{ cursor: order.purchaseData?.shipment_status !== 'Unknown' && order.purchaseData?.purchase_status?.toUpperCase() !== 'CANCELLED' ? 'pointer' : 'default' }}
                          onClick={() => (order.purchaseData?.shipment_status !== 'Unknown' && order.purchaseData?.purchase_status?.toUpperCase() !== 'CANCELLED')
                            ? handleActionClick(order)
                            : undefined}
                        >
                          {(order.purchaseData?.shipment_status !== 'Unknown' && order.purchaseData?.purchase_status?.toUpperCase() !== 'CANCELLED')
                            ? (
                              <>
                                <Typography className={`${styles.actionText} ${isExpanded(order.id) ? styles.actionTextExpanded : ''}`}>
                                  {order.actionText}
                                </Typography>
                                {getActionIcon(order.action, isExpanded(order.id))}
                              </>
                            ) : (
                              <div className={styles.placeholderBox}></div> // Placeholder to maintain position
                            )}
                        </Box>
                      </Box>
                      
                      {/* Expanded Details */}
                      {order.action === 'see_details' && expandedItems.has(order.id) && order.purchaseData && (
                        <Box className={`${styles.orderDetails} ${styles.expanded}`}>
                          {/* Dynamic status steps */}
                          {(() => {
                            const status = order.purchaseData?.shipment_status || 'Order in Progress';
                            let steps;
                            if (status === 'Backorder') {
                              steps = ['Backorder', 'Shipped', 'Out for Delivery', 'Delivered'];
                            } else {
                              steps = ['Order in Progress', 'Shipped', 'Out for Delivery', 'Delivered'];
                            }
                            const currentIndex = steps.indexOf(status);
                            const animateThis = expandedAnimatedIds.has(order.id);
                            if (isMobile) {
                              return (
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                  {steps.map((label, idx) => {
                                    const isActive = idx <= currentIndex;
                                    return (  
                                      <div key={label} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', marginTop: '4px', marginLeft: '5px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                          <div style={{
                                              width: 30,
                                              height: 30,
                                              borderRadius: '50%',
                                              backgroundColor: animateThis && isActive ? '#FD3300' : '#FFE5E0',
                                              transition: 'background-color 0.3s ease',
                                              transitionDelay: `${(idx * 3 + 1) * 0.3}s`
                                            }}
                                          />
                                          <span style={{ marginLeft: 12, fontSize: '16px', fontWeight: 400, color: '#FD3300' }}>
                                            {label}
                                          </span>
                                        </div>
                                        {idx < steps.length - 1 && (
                                          <div style={{
                                              width: 5,
                                              height: 20,
                                              overflow: 'hidden',
                                              marginTop: 4,
                                              marginLeft: 12.5
                                            }}
                                          >
                                            <div style={{
                                                width: '100%',
                                                height: animateThis && isActive ? '100%' : '0%',
                                                backgroundColor: animateThis && isActive ? '#FD3300' : '#FFE5E0',
                                                transition: 'height 0.3s ease',
                                                transitionDelay: `${(idx * 3 + 2) * 0.3}s`
                                            }}
                                            />
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              );
                            }
                            return (
                              <div style={{ display: 'flex' }}>
                                {steps.map((label, idx) => {
                                  const isActive = idx <= currentIndex;
                                  const nextActive = idx + 1 <= currentIndex;
                                  return (
                                    <div
                                      key={label}
                                      style={{
                                        width: '25%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        position: 'relative'
                                      }}
                                    >
                                      {idx > 0 && (
                                        <div
                                          style={{
                                            position: 'absolute',
                                            top: '12.5px',
                                            left: 0,
                                            width: 'calc(50% - 18px)',
                                            height: '5px',
                                            backgroundColor: '#FFE5E0',
                                            overflow: 'hidden'
                                          }}
                                        >
                                          {/* Animated fill bar */}
                                          <div
                                            style={{
                                              width: animateThis && isActive ? '100%' : '0%',
                                              height: '100%',
                                              backgroundColor: '#FD3300',
                                              transition: 'width 0.3s ease',
                                              transitionDelay: `${(idx*3) * 0.3}s`
                                            }}
                                          />
                                        </div>
                                      )}
                                      <div
                                        style={{
                                          width: 30,
                                          height: 30,
                                          borderRadius: '50%',
                                          backgroundColor: animateThis && isActive ? '#FD3300' : '#FFE5E0',
                                          transition: 'background-color 0.3s ease',
                                          transitionDelay: `${(idx*3 + 1) * 0.3}s`
                                        }}
                                      />
                                      <div
                                        style={{
                                          marginTop: 4,
                                          textAlign: 'center',
                                          fontSize: '16px',
                                          fontWeight: '400',
                                          color: '#FD3300'
                                        }}
                                      >
                                        {label}
                                      </div>
                                      {idx < steps.length - 1 && (
                                        <div
                                          style={{
                                            position: 'absolute',
                                            top: '12.5px',
                                            right: 0,
                                            width: 'calc(50% - 18px)',
                                            height: '5px',
                                            backgroundColor: '#FFE5E0',
                                            overflow: 'hidden'
                                          }}
                                        >
                                          <div
                                            style={{
                                              width: animateThis && nextActive ? '100%' : '0%',
                                              height: '100%',
                                              backgroundColor: '#FD3300',
                                              transition: 'width 0.3s ease',
                                              transitionDelay: `${(idx*3+2) * 0.3}s`
                                            }}
                                          />
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })()}
                          <Box className={styles.orderRowDetails}>
                            <Box className={styles.orderLeft}>
                              <Box className={styles.orderInfo}>
                                <Typography className={styles.orderNameDetails}>
                                  Order Status: <u style={{color: "#FD3300", fontWeight: 'bold'}}>{order.purchaseData?.shipment_status ?? 'Unknown'}</u>
                                  <img 
                                    src="https://members.performancegolf.com/wp-content/uploads/2025/12/Icon.png" 
                                    alt="Order status" 
                                    className={styles.questionMarkIcon}
                                    onClick={() => handleOpenPopup(order)}
                                    style={{cursor: 'pointer'}}
                                  />
                                </Typography>
                                <Typography className={styles.statusText}>
                                  Order Date: {order.orderDate}
                                </Typography>
                                </Box>
                              </Box>
                              
                              <Box 
                                className={styles.orderRightTrackingId}
                                onClick={() => order.purchaseData?.carrier_url && window.open(order.purchaseData?.carrier_url || '', '_blank')}
                              >                              
                                <Typography className={`${styles.statusText} ${styles.trackNumber}`}>
                                    Tracking #: <strong style={{color: "#FD3300"}} title={order.purchaseData?.tracking_number || ''}><u>{formatTrackingNumber(order.purchaseData?.tracking_number || 'N/A')}</u></strong>
                                </Typography>
                              </Box>
                            </Box>
                            <hr/>
                          
                          <Box className={styles.imageAndTextContainer}>
                            <img 
                              src={order.purchaseData?.image_path} 
                              alt="Product image"
                              className={styles.orderImage100x100}
                            />
                            <Box className={styles.textLabelsContainer}>
                                <Typography className={styles.productNameTextLabel}>{order.name}</Typography>
                                <Typography className={styles.textLabel}>Product Type: Golf Club</Typography>
                                {(() => {
                                  // Parse variant_name to extract Hand and Staff information
                                  const variantName = order.purchaseData?.variant_name;
                                  
                                  if (!variantName) return null;
                                  
                                  const parts = variantName.split('/').map(part => part.trim());
                                  
                                  return (
                                    <>
                                      {parts[0] && (
                                        <Typography className={styles.textLabel}>Hand: {parts[0]}</Typography>
                                      )}
                                      {parts[1] && (
                                        <Typography className={styles.textLabel}>Staff: {parts[1]}</Typography>
                                      )}
                                    </>
                                  );
                                })()}
                            </Box>
                          </Box>
                          <hr/>

                          <Box className={styles.threeColumnsContainer}>
                            <Box className={styles.columnBox}>
                              <Typography className={styles.productNameTextLabel}>Delivery Address</Typography>
                              <Typography className={styles.textLabel}>
                                  {order.purchaseData?.shipAddress1 || 'N/A'}
                                </Typography>
                                {order.purchaseData?.shipAddress2 && (
                                  <Typography className={styles.textLabel}>
                                    {order.purchaseData.shipAddress2}
                                  </Typography>
                                )}
                                <Typography className={styles.textLabel}>
                                  {order.purchaseData?.shipCity && order.purchaseData?.shipState && order.purchaseData?.shipPostalCode
                                    ? `${order.purchaseData.shipCity}, ${order.purchaseData.shipState} ${order.purchaseData.shipPostalCode} ${order.purchaseData?.shipCountry}`
                                    : 'N/A'
                                  }
                                </Typography>
                            </Box>
                            <Box className={styles.columnBox}>
                              <Typography className={styles.productNameTextLabel}>Contact Information</Typography>
                                <Typography className={styles.textLabel}>
                                  {order.purchaseData?.shipFirstName && order.purchaseData?.shipLastName 
                                    ? `${order.purchaseData.shipFirstName} ${order.purchaseData.shipLastName}`
                                    : '/'
                                  }
                                </Typography>                              
                                <Typography className={styles.textLabel}>{order.purchaseData?.phoneNumber || ''}</Typography>
                                <Typography
                                  className={`${styles.textLabel} ${styles.truncatedEmail}`}
                                  title={order.purchaseData?.emailAddress || ''}
                                >
                                  {order.purchaseData?.emailAddress || ''}
                                </Typography>
                              </Box>
                            <Box className={styles.columnBox}>
                              <Typography className={styles.productNameTextLabel}>Contact Us</Typography>
                              <Button 
                                className={styles.contactUsButton}
                                onClick={() => window.location.href = '/contact-us'}
                              >
                                Contact Support
                              </Button>
                            </Box>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </ListItem>
                  {index < displayedOrders.length - 1 && <Divider className={styles.divider} />}
                </React.Fragment>
              ))}
            </List>
          </>
        )}
      </Box>
    </Box>

    {popupOrder && (
      <Box className={styles.modalOverlay}>
        <Box className={styles.modalContent}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography style={{ fontSize: '24px' }} className={styles.modalContentTitle}>
              Status: <span className={styles.modalTitleStrong}>{popupOrder.purchaseData?.shipment_status ?? 'Unknown'}</span>
            </Typography>
            <IconButton onClick={handleClosePopup}><CloseIcon /></IconButton>
          </Box>
          <Typography className={styles.modalBodyText}>
            {(() => {
              const status = popupOrder.purchaseData?.shipment_status;
              return (
                 status === 'Backorder' ? `This item is currently out of stock due to high demand. We’ve reserved your place in line and will ship your order as soon as the next inventory batch arrives. You’ll receive an update as soon as it’s on the way.` :
                status === 'Shipped' ? `Your order has been shipped and is currently on its way to the delivery address provided. A tracking number has been assigned, allowing you to monitor the progress of your shipment in real time. You’ll receive a confirmation email once your package has been delivered.` :
                status === 'Out for Delivery' ? `Your package has left the local distribution center and is currently out for delivery. You can expect it to arrive at your address soon during the standard delivery window.` :
                status === 'Delivered' ? `Your package has arrived and was delivered to the specified address. You can view the exact delivery time and other details using your tracking link. Thanks for choosing Performance Golf. We look forward to helping you play your best golf.` :
                status === 'Order in Progress' ? `We’re currently preparing your order. This includes processing your items, packaging, and getting everything ready for shipment. Once it's ready to go, you’ll receive an email notification with tracking information.` :
                ''
              );
            })()}
          </Typography>
          <Button variant="contained" className={styles.modalCloseButton} onClick={handleClosePopup} fullWidth>
            Close
          </Button>
          <Button className={styles.modalSupportButton} onClick={() => window.location.href='/contact-us'} fullWidth>
            Contact Support
          </Button>
        </Box>
      </Box>
    )}
    </>
  );
};

export default OrderStatus;
